import { useState, useEffect } from 'react';
import { Lightbulb, Plus, Pencil, Eye, EyeOff, Check, X, Trash2 } from 'lucide-react';
import { useDarkMode } from '../../context/DarkModeContext';
import { api } from '../../lib/api';

interface Tip {
  id: number;
  text: string;
  category: string | null;
  isActive: boolean;
  sortOrder: number;
}

interface TipsTabProps {
  setError: (msg: string) => void;
}

export default function TipsTab({ setError }: TipsTabProps) {
  const { darkMode } = useDarkMode();

  // === Tips State ===
  const [tips, setTips] = useState<Tip[]>([]);
  const [tipsLoading, setTipsLoading] = useState(false);
  const [editingTip, setEditingTip] = useState<Tip | null>(null);
  const [newTipText, setNewTipText] = useState('');
  const [newTipCategory, setNewTipCategory] = useState('');
  const [showAddTip, setShowAddTip] = useState(false);
  const [tipActionLoading, setTipActionLoading] = useState<number | null>(null);
  const [confirmDeleteTip, setConfirmDeleteTip] = useState<Tip | null>(null);

  useEffect(() => {
    void loadTips();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // === Tips Logic ===
  const loadTips = async () => {
    try {
      setTipsLoading(true);
      const data = await api.get<Tip[]>('/api/tips');
      setTips(data);
    } catch {
      setError('Fehler beim Laden der Tipps');
    } finally {
      setTipsLoading(false);
    }
  };

  const handleAddTip = async () => {
    if (!newTipText.trim()) return;
    try {
      const maxOrder = tips.reduce((max, t) => Math.max(max, t.sortOrder), 0);
      const created = await api.post<Tip>('/api/tips', {
        text: newTipText.trim(),
        category: newTipCategory.trim() || null,
        sortOrder: maxOrder + 1,
      });
      setTips(prev => [...prev, created]);
      setNewTipText('');
      setNewTipCategory('');
      setShowAddTip(false);
    } catch {
      setError('Fehler beim Erstellen des Tipps');
    }
  };

  const handleUpdateTip = async (tip: Tip) => {
    try {
      setTipActionLoading(tip.id);
      const updated = await api.patch<Tip>(`/api/tips/${tip.id}`, {
        text: tip.text,
        category: tip.category,
        isActive: tip.isActive,
        sortOrder: tip.sortOrder,
      });
      setTips(prev => prev.map(t => t.id === tip.id ? updated : t));
      setEditingTip(null);
    } catch {
      setError('Fehler beim Aktualisieren des Tipps');
    } finally {
      setTipActionLoading(null);
    }
  };

  const handleToggleTipActive = async (tip: Tip) => {
    try {
      setTipActionLoading(tip.id);
      const updated = await api.patch<Tip>(`/api/tips/${tip.id}`, { isActive: !tip.isActive });
      setTips(prev => prev.map(t => t.id === tip.id ? updated : t));
    } catch {
      setError('Fehler beim Aktualisieren');
    } finally {
      setTipActionLoading(null);
    }
  };

  const handleDeleteTip = async (tip: Tip) => {
    try {
      setTipActionLoading(tip.id);
      setConfirmDeleteTip(null);
      await api.delete(`/api/tips/${tip.id}`);
      setTips(prev => prev.filter(t => t.id !== tip.id));
    } catch {
      setError('Fehler beim Löschen des Tipps');
    } finally {
      setTipActionLoading(null);
    }
  };

  const card = darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';
  const inputClass = `w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    darkMode ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'bg-white border-slate-300 placeholder-slate-400'
  }`;

  // Get unique categories for quick-select
  const categories = [...new Set(tips.map(t => t.category).filter(Boolean))] as string[];

  return (
    <>
      {/* Header mit Add-Button */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Tipps werden Nicht-Admin-Benutzern während der Textgenerierung angezeigt.
          </p>
        </div>
        <button
          onClick={() => setShowAddTip(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
        >
          <Plus className="w-4 h-4" />
          Neuer Tipp
        </button>
      </div>

      {/* Add Tip Form */}
      {showAddTip && (
        <div className={`rounded-xl border p-4 mb-4 ${card}`}>
          <h3 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
            Neuen Tipp erstellen
          </h3>
          <div className="space-y-3">
            <div>
              <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Tipp-Text *
              </label>
              <textarea
                value={newTipText}
                onChange={e => setNewTipText(e.target.value)}
                rows={3}
                placeholder="Geben Sie hier den Tipp-Text ein..."
                className={`${inputClass} resize-y`}
              />
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Kategorie (optional)
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newTipCategory}
                  onChange={e => setNewTipCategory(e.target.value)}
                  placeholder="z.B. Allgemein, Abschnitt 2, Fristen..."
                  className={inputClass}
                />
              </div>
              {categories.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {categories.map(cat => (
                    <button
                      key={cat}
                      onClick={() => setNewTipCategory(cat)}
                      className={`text-xs px-2 py-0.5 rounded-full transition-colors ${
                        newTipCategory === cat
                          ? 'bg-blue-600 text-white'
                          : darkMode ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setShowAddTip(false); setNewTipText(''); setNewTipCategory(''); }}
                className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                  darkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-600 hover:bg-slate-50'
                }`}
              >
                Abbrechen
              </button>
              <button
                onClick={handleAddTip}
                disabled={!newTipText.trim()}
                className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50"
              >
                Speichern
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tips List */}
      <div className="space-y-2">
        {tipsLoading ? (
          <div className={`rounded-xl border p-8 text-center ${card}`}>
            <p className="text-slate-400">Lädt...</p>
          </div>
        ) : tips.length === 0 ? (
          <div className={`rounded-xl border p-8 text-center ${card}`}>
            <Lightbulb className={`w-8 h-8 mx-auto mb-2 ${darkMode ? 'text-slate-600' : 'text-slate-300'}`} />
            <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Noch keine Tipps vorhanden. Standard-Tipps werden beim Neustart des Servers erstellt.
            </p>
          </div>
        ) : tips.map(tip => (
          <div
            key={tip.id}
            className={`rounded-xl border p-4 transition-all ${
              !tip.isActive ? 'opacity-50' : ''
            } ${card} ${
              editingTip?.id === tip.id
                ? darkMode ? 'ring-2 ring-blue-500' : 'ring-2 ring-blue-400'
                : ''
            }`}
          >
            {editingTip?.id === tip.id ? (
              /* Edit Mode */
              <div className="space-y-3">
                <textarea
                  value={editingTip.text}
                  onChange={e => setEditingTip({ ...editingTip, text: e.target.value })}
                  rows={3}
                  className={`${inputClass} resize-y`}
                />
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={editingTip.category || ''}
                    onChange={e => setEditingTip({ ...editingTip, category: e.target.value || null })}
                    placeholder="Kategorie"
                    className={inputClass}
                  />
                </div>
                {categories.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {categories.map(cat => (
                      <button
                        key={cat}
                        onClick={() => setEditingTip({ ...editingTip, category: cat })}
                        className={`text-xs px-2 py-0.5 rounded-full transition-colors ${
                          editingTip.category === cat
                            ? 'bg-blue-600 text-white'
                            : darkMode ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setEditingTip(null)}
                    className={`p-1.5 rounded-lg transition-colors ${
                      darkMode ? 'text-slate-400 hover:bg-slate-700' : 'text-slate-500 hover:bg-slate-100'
                    }`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleUpdateTip(editingTip)}
                    disabled={tipActionLoading === tip.id}
                    className="p-1.5 rounded-lg text-green-600 hover:bg-green-100 transition-colors"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ) : (
              /* Display Mode */
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  {tip.category && (
                    <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full mb-1.5 ${
                      darkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-50 text-blue-600'
                    }`}>
                      {tip.category}
                    </span>
                  )}
                  <p className={`text-sm leading-relaxed ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    {tip.text}
                  </p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => handleToggleTipActive(tip)}
                    disabled={tipActionLoading === tip.id}
                    title={tip.isActive ? 'Deaktivieren' : 'Aktivieren'}
                    className={`p-1.5 rounded-lg transition-colors ${
                      tip.isActive
                        ? 'text-green-600 hover:bg-green-100'
                        : darkMode ? 'text-slate-500 hover:bg-slate-700' : 'text-slate-400 hover:bg-slate-100'
                    }`}
                  >
                    {tip.isActive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => setEditingTip({ ...tip })}
                    title="Bearbeiten"
                    className={`p-1.5 rounded-lg transition-colors ${
                      darkMode ? 'text-slate-400 hover:bg-slate-700' : 'text-slate-500 hover:bg-slate-100'
                    }`}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setConfirmDeleteTip(tip)}
                    disabled={tipActionLoading === tip.id}
                    title="Löschen"
                    className="p-1.5 rounded-lg text-red-500 hover:bg-red-100 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <p className={`mt-4 text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
        {tips.length} Tipps · {tips.filter(t => t.isActive).length} aktiv · Tipps rotieren alle 10 Sekunden während der Textgenerierung
      </p>

      {/* Lösch-Bestätigung (Tip) */}
      {confirmDeleteTip && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className={`rounded-xl border shadow-xl p-6 max-w-sm mx-4 ${card}`}>
            <h3 className="text-lg font-semibold mb-2">Tipp löschen?</h3>
            <p className={`text-sm mb-2 ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              Möchten Sie diesen Tipp wirklich löschen?
            </p>
            <p className={`text-xs mb-6 p-2 rounded-lg ${darkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
              {confirmDeleteTip.text.length > 100 ? confirmDeleteTip.text.slice(0, 100) + '...' : confirmDeleteTip.text}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDeleteTip(null)}
                className={`flex-1 px-4 py-2 rounded-lg border text-sm font-medium ${darkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-700 hover:bg-slate-50'}`}
              >
                Abbrechen
              </button>
              <button
                onClick={() => handleDeleteTip(confirmDeleteTip)}
                className="flex-1 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700"
              >
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
